import React, { useEffect, useId } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

import { fetchVideoCipherVideoOTP, saveCurrentTimeStamp } from '../../redux/slices/resourceDashboard';

function VdoCipherPlayer({ video, triggerEvent, playerRef }) {
  const dispatch = useDispatch();
  const playerId = useId();

  const { videoOtpInfo } = useSelector((state) => ({
    videoOtpInfo: state.resourceDashboard?.videoInfo?.otpInfo,
  }));

  // get VdoCipher otp
  useEffect(() => {
    if (video?.referenceId) {
      dispatch(fetchVideoCipherVideoOTP(video?.referenceId));
    }
  }, [dispatch, video?.referenceId]);

  const getPlayerTrackingData = React.useCallback(() => {
    const { current: currentPlayerRef } = playerRef;

    const playerQuality = currentPlayerRef.availableQualities?.find((quality) => quality.active) || { bandwidth: 0 };

    return {
      video_id: video?.referenceId,
      video_player: 'video_cipher',
      topic_title: currentPlayerRef.title,
      selected_speed: currentPlayerRef.playbackRate,
      selected_quality: `${playerQuality.bandwidth / 1000} kbps`,
      seek_value: 5,
      lastTimestamp: currentPlayerRef.currentTime,
      videoTotalTime: currentPlayerRef.duration,
    };
  }, [playerRef, video?.referenceId]);

  // VdoCipher data
  useEffect(() => {
    const { otp, playbackInfo } = videoOtpInfo || {};

    if (typeof window !== 'undefined' && typeof VdoPlayer !== 'undefined' && otp && playbackInfo && playerId) {
      const currentPlayerRef = new window.VdoPlayer({
        otp,
        playbackInfo,
        theme: '9ae8bbe8dd964ddc9bdb932cca1cb59a',
        container: document.getElementById(`player-${playerId}`),
        plugins: [
          {
            name: 'keyboard',
            options: {
              preset: 'default',
              bindings: {
                Up: (player) => player.setVolume(player.volume + 0.2),
                Down: (player) => player.setVolume(player.volume - 0.2),
              },
            },
          },
        ],
        // autoplay: true,
      });
      currentPlayerRef.addEventListener('play', () => {
        if (typeof triggerEvent === 'function') {
          triggerEvent('play', {
            player_action: 'play',
            ...getPlayerTrackingData(),
          });
        }
      });

      currentPlayerRef.addEventListener('pause', () => {
        if (typeof triggerEvent === 'function') {
          triggerEvent('pause', {
            player_action: 'pause',
            ...getPlayerTrackingData(),
          });
        }
      });

      let seekCount = 0;
      currentPlayerRef.addEventListener('seeking', () => {
        if (typeof triggerEvent === 'function') {
          seekCount += 1;
          if (seekCount === 1) {
            setTimeout(() => {
              dispatch(saveCurrentTimeStamp(currentPlayerRef.currentTime));
            }, 500);
            triggerEvent('seeking', {
              player_action: 'seeking',
              ...getPlayerTrackingData(),
            });
          }
          setTimeout(() => {
            seekCount = 0;
          }, 2000);
        }
      });

      playerRef.current = currentPlayerRef;
    }
  }, [playerId, getPlayerTrackingData, playerRef, triggerEvent, videoOtpInfo, dispatch]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const playerElement = document.getElementById(`player-${playerId}`);

    if (playerElement) {
      const handleKeyPress = (event) => {
        const { current: currentPlayerRef } = playerRef;

        if (event.which === 77) {
          event.preventDefault();
          if (currentPlayerRef?.muted) {
            currentPlayerRef.unmute();
          } else {
            currentPlayerRef.mute();
          }
        } else if (event.which === 76) {
          event.preventDefault();
          currentPlayerRef.seek(currentPlayerRef.currentTime + 5);
        } else if (event.which === 74) {
          event.preventDefault();
          currentPlayerRef.seek(currentPlayerRef.currentTime - 5);
        }
      };

      playerElement.addEventListener('keydown', handleKeyPress);

      return () => playerElement.removeEventListener('keydown', handleKeyPress);
    }
  }, []);

  return <div id={`player-${playerId}`} className="new_vdocipher_player" />;
}

export default VdoCipherPlayer;
